import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';

import { ShipmentDetailsPrintDocumentButton } from '../../../../containers/print-document-button-containers/shipment-details';
import ShipmentStatusHeader from '../../../../components/shipment-status-header';

import { ShipmentStatusService } from '../../../../services/shipment-status-service';
import { FulfillmentPlanFulfillmentType } from '../../../../enums/fulfillment-plan-fulfillment-type';
import { FulfillmentPlanService } from '../../../../services/fulfillment-plan-service';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { ShipmentDetailsBody } from './shipment-details-body';
import { OtherActionsButton } from './other-actions-button';

import style from './shipment-details-modal-content.module.less';
import { CreateReturnButton } from './create-return-button';

export const ShipmentDetailsModalContent = ({
  isLoading,
  isShipmentFromCustomerDetails = false,
  closeModal,
  actions = {
    printPackingSlip: noop,
    printPreview: noop,
    packingSlipPrintAction: noop,
    launchVoidLabelModal: noop,
    labelPrintedAction: noop,
    createReturnsFromShipment: noop,
  },
  fulfillmentPlan = emptyObject,
  activeFulfillment,
  fulfillments = emptyArray,
  hasActiveLabel = false,
  salesOrders = emptyArray,
  isRefundAssistView = false,
}) => {
  const onClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  if (isLoading) {
    return (
      <div className={classnames(style['shipment-details-modal-content'], style.loading)}>
        <LoadingIndicator />
      </div>
    );
  }

  const fulfillmentPlanId = fulfillmentPlan.fulfillmentPlanId || 0;
  const fulfillmentType = fulfillmentPlan?.fulfillmentType ?? '';
  const markedComplete = fulfillmentType === FulfillmentPlanFulfillmentType.Manual;
  const shipmentStatus = ShipmentStatusService.buildStatusFromFulfillments(fulfillmentPlan, fulfillments);
  const nonVoidedFulfillments = fulfillments.filter((fulfillment) => !fulfillment.isLabelVoided);
  const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
  const trackingNumber = nonVoidedFulfillment?.trackingInformation?.trackingNumber ?? null;
  const isCombo = salesOrders?.length > 1;
  const hasLinkedSalesOrder = salesOrders?.length > 0;
  const isReturnsDisabled = !(hasLinkedSalesOrder && hasActiveLabel);
  const firstSalesOrderId = salesOrders[0]?.salesOrderId;

  return (
    <div className={style['shipment-details-modal-content']}>
      <div className={style['content-header']}>
        <ShipmentStatusHeader
          actions={actions}
          fulfillmentId={fulfillments.map((fulfillment) => fulfillment.fulfillmentId).find((fulfillmentId) => !!fulfillmentId)}
          fulfillmentPlanId={fulfillmentPlanId}
          isCombo={isCombo}
          isRefundAssistView={isRefundAssistView}
          isThirdPartyFulfillment={FulfillmentPlanService.isExternal(fulfillmentPlan)}
          markedComplete={markedComplete}
          onClose={onClose}
          shipDateTime={fulfillmentPlan.shipDateTime}
          shipmentStatus={shipmentStatus}
        >
          <div className={style['shipment-details-header-buttons']}>
            <ShipmentDetailsPrintDocumentButton data={{ fulfillments: nonVoidedFulfillments, fulfillmentPlan }} />
            <OtherActionsButton
              actions={actions}
              activeFulfillment={activeFulfillment}
              fulfillmentPlan={fulfillmentPlan}
              fulfillments={fulfillments}
              isShipmentFromCustomerDetails={isShipmentFromCustomerDetails}
              markedComplete={markedComplete}
              orderGridStatus={fulfillmentPlan.orderGridStatus}
              shipmentStatus={shipmentStatus}
              trackingNumber={trackingNumber}
            />
            <CreateReturnButton
              disabled={isReturnsDisabled}
              fulfillmentPlanId={fulfillmentPlanId}
              hasToolTip={!hasLinkedSalesOrder}
              salesOrderId={firstSalesOrderId}
            />
          </div>
        </ShipmentStatusHeader>
      </div>
      <ShipmentDetailsBody
        actions={actions}
        fulfillmentPlan={fulfillmentPlan}
        fulfillments={fulfillments}
        hasActiveLabel={hasActiveLabel}
        isRefundAssistView={isRefundAssistView}
        salesOrders={salesOrders}
      />
    </div>
  );
};

ShipmentDetailsModalContent.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  actions: PropTypes.object,
  salesOrders: PropTypes.array,
  fulfillmentPlan: PropTypes.object,
  activeFulfillment: PropTypes.object,
  fulfillments: PropTypes.array,
  isShipmentFromCustomerDetails: PropTypes.bool,
  hasActiveLabel: PropTypes.bool,
  isRefundAssistView: PropTypes.bool,
};
